import cn from 'classnames';
import { type FC } from 'react';
import { useSelector } from 'react-redux';

import { AccordionHeader, AccordionItem, AccordionPanel } from '@/components/UI/Accordion/Accordion';
import Link from '@/components/UI/Link';
import { _isNotBlank } from '@/littledash';
import type { Study, StudyOverviewSectionV1 } from '@/model/Study.model';
import { selectFeatures } from '@/reducers/team';

import type { SuccessfulOverviewSection } from '../OverviewAccordion.model';
import { isEmptyOverviewSection, overviewSectionManagePath, overviewSectionTitle } from '../OverviewAccordion.utils';
import AlertsSectionContent from '../contents/AlertsSectionContent/AlertsSectionContent';
import GroupsSectionContent from '../contents/GroupsSectionContent/GroupsSectionContent';
import MetadataSectionContent from '../contents/MetadataSectionContent/MetadataSectionContent';
import PresetSectionContent from '../contents/PresetSectionContent/PresetSectionContent';
import SamplingPlanSectionContent from '../contents/SamplingPlanSectionContent/SamplingPlanSectionContent';
import TasksSectionContent from '../contents/TasksSectionContent/TasksSectionContent';
import TreatmentsSectionContent from '../contents/TreatmentsSectionContent/TreatmentsSectionContent';
import { canManageOverviewSection } from '../overviewSectionAccess';
import styles from './OverviewAccordionSection.module.scss';

interface OverviewAccordionSectionProps {
  section: StudyOverviewSectionV1;
  sectionId: string;
  index?: number;
  study?: Study;
  studyId?: string;
  onSectionSaved?: () => void;
}

interface SectionContentProps {
  section: SuccessfulOverviewSection;
  study?: Study;
  studyId?: string;
  diseaseInduction: boolean;
  onSectionSaved?: () => void;
}

const SectionContent: FC<SectionContentProps> = ({ section, study, studyId, diseaseInduction, onSectionSaved }) => {
  switch (section.type) {
    case 'metadata':
      return <MetadataSectionContent section={section} />;
    case 'groups':
      return <GroupsSectionContent section={section} />;
    case 'diseases':
    case 'treatments':
      return (
        <TreatmentsSectionContent
          section={section}
          study={study}
          diseaseInduction={diseaseInduction}
          onTreatmentSaved={onSectionSaved}
        />
      );
    case 'sampling_plan':
      return <SamplingPlanSectionContent section={section} />;
    case 'tasks':
      return <TasksSectionContent section={section} study={study} studyId={studyId} onTaskSaved={onSectionSaved} />;
    case 'preset':
      return <PresetSectionContent section={section} />;
    case 'alerts':
      return <AlertsSectionContent section={section} study={study} onAlertSaved={onSectionSaved} />;
  }
};

/**
 * One study-design section in the Overview accordion: its header (title, count,
 * Manage link) and a panel that shows the content component for the section type.
 * Must be a direct child of `Accordion`, which injects `index`.
 */
const OverviewAccordionSection: FC<OverviewAccordionSectionProps> = ({
  section,
  sectionId,
  index,
  study,
  studyId,
  onSectionSaved,
}) => {
  const diseaseInduction = useSelector(selectFeatures)?.disease_induction === true;
  const empty = isEmptyOverviewSection(section);
  const managePath =
    !empty && _isNotBlank(studyId) && canManageOverviewSection(section.type, study, diseaseInduction)
      ? overviewSectionManagePath(section.type, studyId)
      : null;

  return (
    <AccordionItem index={index} className={cn(styles.item, { [styles.disabled]: empty })} disabled={empty}>
      <AccordionHeader className={styles.header} testId={`overview-accordion-${sectionId}-header`}>
        <span className={styles.title}>{overviewSectionTitle(section)}</span>
        {section.status === 'success' && <span className={styles.count}>({section.count})</span>}
        {_isNotBlank(managePath) && (
          <Link
            className={cn('link', styles.manage)}
            to={managePath}
            data-testid={`overview-accordion-${sectionId}-manage`}
            onClick={(event) => event.stopPropagation()}
          >
            Manage
          </Link>
        )}
      </AccordionHeader>
      <AccordionPanel className={styles.panel}>
        {section.status === 'error' ? (
          <p className={styles.error} role="status">
            Unable to load this section.
          </p>
        ) : (
          <SectionContent
            section={section}
            study={study}
            studyId={studyId}
            diseaseInduction={diseaseInduction}
            onSectionSaved={onSectionSaved}
          />
        )}
      </AccordionPanel>
    </AccordionItem>
  );
};

export default OverviewAccordionSection;
